import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (!user) return;
    const isMember = user.role === "admin" || user.subscription_status === "active";
    navigate(!isMember && !user.free_case_id ? "/welcome" : "/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.name, form.email, form.password, marketingConsent, ref);
      toast.success(isLogin ? "Welcome back, investigator." : "Account created.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/8168566/pexels-photo-8168566.png" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/85 grain" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/"><Logo /></Link>
          <div className="max-w-md">
            <h2 className="font-display text-3xl font-bold text-white leading-tight">Where the case file lives.</h2>
            <p className="mt-4 text-white/60">Organized documents, interactive timelines, guarded AI analysis, and a community that discusses evidence — not rumors.</p>
          </div>
          <p className="label-mono">The Investigator Bullpen</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-charcoal">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Link to="/"><Logo /></Link></div>
          <div className="label-mono mb-2">{isLogin ? "Sign in" : "Create account"}</div>
          <h1 className="font-display text-3xl font-bold text-white mb-8">
            {isLogin ? "Access the workspace" : "Join the investigation"}
          </h1>

          {!isLogin && ref && (
            <div data-testid="referral-banner" className="mb-6 flex items-center gap-2.5 rounded-sm border border-gold/30 bg-gold/5 px-4 py-3">
              <Gift className="w-4 h-4 text-gold shrink-0" />
              <p className="text-xs text-white/70">You were invited by a fellow investigator. Welcome to the bullpen!</p>
            </div>
          )}

          <button onClick={googleLogin} data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 px-4 py-3 mb-5 rounded-sm border border-white/15 text-white hover:border-gold/40 hover:bg-white/5 transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            <span className="text-sm font-medium">Continue with Google</span>
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="label-mono">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label className="label-mono">Name</Label>
                <Input data-testid="auth-name-input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1.5 bg-charcoal border-white/15 focus-visible:ring-gold text-white" placeholder="Jane Doe" />
              </div>
            )}
            <div>
              <Label className="label-mono">Email</Label>
              <Input data-testid="auth-email-input" type="email" required autoComplete="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="mt-1.5 bg-charcoal border-white/15 focus-visible:ring-gold text-white" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="label-mono">Password</Label>
              <div className="relative mt-1.5">
                <Input data-testid="auth-password-input" type={showPw ? "text" : "password"} required autoComplete={isLogin ? "current-password" : "new-password"} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className="bg-charcoal border-white/15 focus-visible:ring-gold text-white pr-10" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="toggle-password-btn"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-white/40 hover:text-gold transition-colors">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
            {isLogin && (
              <div className="text-right -mt-1">
                <Link to="/forgot-password" className="text-xs text-white/50 hover:text-gold transition-colors" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
            )}
            {!isLogin && (
              <label className="flex items-start gap-2.5 cursor-pointer" data-testid="marketing-consent-label">
                <input
                  type="checkbox"
                  data-testid="marketing-consent-checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-gold cursor-pointer"
                />
                <span className="text-xs text-white/50 leading-relaxed">
                  Yes, email me case updates, new videos, live-call invites and occasional offers. I agree Help Solve The Case may use my information to contact me and for marketing. You can unsubscribe anytime.
                </span>
              </label>
            )}
            <Button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="w-full bg-gold text-charcoal hover:bg-gold-hover font-semibold rounded-sm">
              {loading ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-white/50 text-center">
            {isLogin ? "New here? " : "Already a member? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-gold hover:text-gold-hover" data-testid="auth-toggle-link">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
